// markingStyle.js — shared marking → OL Style + tactical-graphic SIDC-expansion glue.
//
// This is the SINGLE implementation of "how a marking becomes OL features/styles", consumed by
// BOTH rendering pipelines that used to duplicate it:
//   - the live editor (useMapEditor.js: featureStyleFn/tacticalStyleFn/syncTacticalSource)
//   - the report-embed preview (MapPreviewEngine.js: featureStyle/symbolStyle), which previously
//     had its OWN copy of unit-icon styling and NO tactical-graphic expansion at all — the root
//     cause of tactical graphics rendering as a plain dot/line/blob in report-embed previews.
//
// Two responsibilities live here:
//   1. Unit-icon (mil-sym SVG) OL Style — built on SymbolRenderer.js's shared buildModsMap/
//      buildAttrsMap (same mil-sym-ts-web MilStdIconRenderer.RenderSVG call SymbolGallery/
//      SymbolEditor/etc. already go through) plus the OL Icon-anchor math + style cache.
//   2. Tactical-graphic SIDC expansion: turns a tactical-line/-area/-point marking's stored
//      sidc+coords into the fully expanded multi-feature GeoJSON (arrowheads, echelon ticks,
//      dashes, etc.) via TacticalRenderer.js's renderTacticalGraphic (already shared), plus the
//      OL Style function for the resulting sub-features (ported from useMapEditor's
//      tacticalStyleFn, unchanged).

import { Style, Fill, Stroke, Circle as CircleStyle, Icon, Text as OLText } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { MilStdIconRenderer } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import { buildModsMap, buildAttrsMap } from './SymbolRenderer';
import { renderTacticalGraphic } from './TacticalRenderer';
import { getFillVariant } from './fillVariant';
import { applyStaleRendering } from './staleRendering';
import { resolveTrackAtDtg } from '../../schema/mapPmAdapter';

const FORMAT = new GeoJSON({ featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });

// ---------------------------------------------------------------------------
// Unit-icon styling — OL Icon style from mil-sym-ts-web SVG data URI.
// ponytail: OL caches Icon styles by src string, so repeated calls for the
// same SIDC are effectively free - no atlas management needed.
//
// Ported from useMapEditor.js's symbolStyleForSidc and MapPreviewEngine.js's symbolStyle (the
// two divergent copies this module replaces) — same RenderSVG + anchor-math + cache shape as
// both, built on SymbolRenderer.js's shared buildModsMap/buildAttrsMap.
// ---------------------------------------------------------------------------
const symbolStyleCache = new Map();

export function clearSymbolStyleCache() {
    symbolStyleCache.clear();
}

/**
 * Build (or return a cached) OL Style for a unit-icon marking's SIDC.
 *
 * @param {string} sidc
 * @param {object} mods - plain object keyed by Modifiers.* constants
 * @returns {import('ol/style').Style|null}
 */
export function symbolStyleForSidc(sidc, mods) {
    const key = `${sidc}|${JSON.stringify(mods)}`;
    if (symbolStyleCache.has(key)) return symbolStyleCache.get(key);

    try {
        const renderer = MilStdIconRenderer.getInstance();
        if (!renderer) return null;
        const modsMap = buildModsMap(mods);
        const attrsMap = buildAttrsMap();
        const info = renderer.RenderSVG(sidc, modsMap, attrsMap);
        if (!info) return null;
        const src = info.getSVGDataURI();
        const cx = (info.getSymbolCenterX() ?? 0) / (info.getImageBounds()?.width ?? 1);
        const cy = (info.getSymbolCenterY() ?? 0) / (info.getImageBounds()?.height ?? 1);
        const style = new Style({
            image: new Icon({ src, anchor: [cx, cy], anchorXUnits: 'fraction', anchorYUnits: 'fraction' }),
        });
        symbolStyleCache.set(key, style);
        return style;
    } catch (_) {
        return null;
    }
}

/**
 * Style function for the main markings layer (unit-icons via mil-sym SVG Icon style,
 * points/lines/polygons via basic OL styles). Ported verbatim from useMapEditor.js's
 * featureStyleFn — same behavior, just relocated so MapPreviewEngine can call it too.
 *
 * `selectedIds` is optional (the preview path has no selection concept) and only affects the
 * unused-in-preview `isSelected` local — kept for parity/future use, matches prior behavior.
 *
 * @param {import('ol/Feature').default} feature
 * @param {Set<string>} [selectedIds]
 * @returns {import('ol/style').Style|Array<import('ol/style').Style>|null}
 */
export function markingFeatureStyle(feature, selectedIds) {
    const props = feature.get('_props') || {};
    const elemType = props.elementType;
    const color = props.color || '#3b82f6';
    const isSelected = selectedIds?.has(String(props.markingId));
    const geomType = feature.getGeometry()?.getType?.();
    // ticket 04: markingsToGeoJSON (mapPmAdapter.js) stamps `isCurrent: false` on a `moving`
    // track whose DTG-cursor-resolved snapshot is stale ("last known") relative to what's
    // known — apply the shared fade+badge treatment right before returning any base style.
    const stale = props.isCurrent === false;

    if (elemType === 'unit-icon' && props.sidc) {
        const mods = props.sidcModifiers
            ? (typeof props.sidcModifiers === 'string' ? JSON.parse(props.sidcModifiers) : props.sidcModifiers)
            : {};
        const style = symbolStyleForSidc(props.sidc, mods);
        if (style) return stale ? applyStaleRendering(style) : style;
    }

    if (geomType === 'Point') {
        const style = new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color }), stroke: new Stroke({ color: '#fff', width: 1.5 }) }) });
        return stale ? applyStaleRendering(style) : style;
    }

    if (geomType === 'LineString') {
        const style = new Style({ stroke: new Stroke({ color, width: 2 }) });
        return stale ? applyStaleRendering(style) : style;
    }

    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        const strokeColor = isSelected ? '#fff' : color;
        const strokeWidth = isSelected ? 3 : 2;
        const styles = [
            new Style({ fill: new Fill({ color: color + '1f' }), stroke: new Stroke({ color: strokeColor, width: strokeWidth }) }),
        ];
        return stale ? applyStaleRendering(styles) : styles;
    }
    return null;
}

// ---------------------------------------------------------------------------
// Tactical-graphic styling — ported verbatim from useMapEditor.js's tacticalStyleFn/
// parseDasharray/hexWithOpacity.
// ---------------------------------------------------------------------------

// ponytail: parse WebRenderer's CSS-string strokeDasharray ("8 4") → OL number array [8, 4]
function parseDasharray(val) {
    if (!val) return undefined;
    if (Array.isArray(val)) return val.map(Number);
    const nums = String(val).trim().split(/[\s,]+/).map(Number).filter((n) => !isNaN(n) && n >= 0);
    return nums.length ? nums : undefined;
}

// ponytail: convert hex "#rrggbb" + opacity float → "rgba(r,g,b,a)" for OL Fill/Stroke. Exported
// so useMapEditor.js's trailStyleFn (movement-trail connecting line) can reuse it too, rather
// than keeping a second copy — the only other caller of this helper.
export function hexWithOpacity(hex, opacity) {
    if (!hex) return null;
    if (!hex.startsWith('#') || hex.length !== 7) return hex; // pass through rgba/named
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity ?? 1})`;
}

/**
 * Style function for tactical-graphic sub-features (the expanded GeoJSON produced by
 * `expandTacticalMarkingsToFeatures` below). Ported verbatim from useMapEditor.js's
 * tacticalStyleFn.
 *
 * @param {import('ol/Feature').default} feature
 * @returns {import('ol/style').Style|null}
 */
export function tacticalFeatureStyle(feature) {
    const p = feature.get('_props') || {};
    const geomType = feature.getGeometry()?.getType?.();
    // ticket 04: syncTacticalSource stamps `isCurrent: false` on a `moving` track's tactical
    // graphic whose DTG-cursor-resolved snapshot is stale ("last known") — apply the shared
    // fade+badge treatment right before returning the base style below.
    const stale = p.isCurrent === false;

    // ── Stroke ──────────────────────────────────────────────────────────────
    const strokeColor = p.strokeColor || '#000000';
    const strokeWidth = p.strokeWidth ?? p.strokeWeight ?? 2;
    const lineOpacity = p.lineOpacity ?? 1;
    const lineDash = parseDasharray(p.strokeDasharray);
    const stroke = new Stroke({
        color: hexWithOpacity(strokeColor, lineOpacity),
        width: strokeWidth,
        lineDash,
        lineCap: p.lineCap === 1 ? 'butt' : p.lineCap === 2 ? 'square' : 'round',
    });

    // ── Text label ───────────────────────────────────────────────────────────
    let text = null;
    if (p.label) {
        text = new OLText({
            text: p.label,
            fill: new Fill({ color: p.fontColor || '#000000' }),
            stroke: p.labelOutlineColor ? new Stroke({ color: p.labelOutlineColor, width: p.labelOutlineWidth ?? 2 }) : undefined,
            font: `${p.fontWeight || 'normal'} ${p.fontSize || 11}px ${p.fontFamily || 'sans-serif'}`,
        });
    }

    let style;
    if (geomType === 'Point') {
        // WebRenderer's image modifiers (BOMB/LAA/AA/NAI anchors, etc.) already come as a
        // ready `data:image/svg+xml;base64,...` URI — see Modifier2.getModifierImage() -> SVGSymbolInfo.getSVGDataURI().
        if (p.image) {
            const rotationDeg = p.rotation ?? p.angle ?? 0;
            style = new Style({
                image: new Icon({ src: p.image, rotation: (rotationDeg * Math.PI) / 180 }),
                text,
            });
        } else {
            const radius = p.pointRadius ?? 4;
            style = new Style({
                image: new CircleStyle({ radius, fill: new Fill({ color: hexWithOpacity(strokeColor, lineOpacity) }), stroke: new Stroke({ color: '#fff', width: 1 }) }),
                text,
            });
        }
    } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
        style = new Style({ stroke, text });
    } else if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        const sidc = p.tacticalSidc;
        // Variant B (hatch) and C (perimeter-stamp) are drawn by AreaFillOverlay
        // on the postrender canvas pass — suppress the default OL fill so the
        // custom drawing is the sole paint source for those variants.
        const variant = sidc ? getFillVariant(sidc) : 'solid';
        let fill = null;
        if (variant === 'solid') {
            // Prefer an explicit fillColor from the renderer properties; fall back
            // to the stroke color at 12% opacity (MIL-STD identity palette is already
            // baked into strokeColor by the WebRenderer).

            const baseColor = p.fillColor || p.strokeColor || '#000000';
            const opacity = p.fillOpacity ?? (p.fillColor ? 0.25 : 0.12);
            fill = new Fill({ color: hexWithOpacity(baseColor, opacity) });
        }
        style = new Style({ fill, stroke, text });
    }
    if (!style) return null;
    return stale ? applyStaleRendering(style) : style;
}

// ---------------------------------------------------------------------------
// Tactical-graphic SIDC expansion — shared step both pipelines run before styling.
// ---------------------------------------------------------------------------

/**
 * Expand every tactical-line/-area/-point marking in `markings` into its fully-rendered
 * multipoint GeoJSON (arrowheads, echelon ticks, dashes, etc.) via TacticalRenderer's
 * renderTacticalGraphic, resolved against `cursorDtg`/trail settings exactly like a regular
 * marking (see resolveTrackAtDtg). Returns plain GeoJSON Feature objects — never OL Features —
 * so this is usable from both the live editor (which then wraps each in an OL Feature to add to
 * LYR_TACTICAL) and the preview path (which can feed them straight into `markingsToGeoJSON`'s
 * output feature array, or read them standalone).
 *
 * This is the ONE tactical-expansion implementation — the logic previously lived only inside
 * useMapEditor.js's syncTacticalSource; the preview path (MapPreviewEngine.js) had NO equivalent
 * at all, which was the root cause of tactical graphics rendering as a plain fallback dot/line/
 * blob in report-embed previews.
 *
 * @param {Array} markings - `map_marking` node attrs (as from extractMapState).
 * @param {object} [viewportOpts] - `{pixelWidth, pixelHeight, viewportBbox}`, forwarded as-is to
 *   renderTacticalGraphic (see TacticalRenderer.js). Falls back to its own 800x600/tight-bbox
 *   default when omitted — used by the preview path, which has no live OL map/viewport.
 * @param {number|null} [cursorDtg] - epoch ms DTG cursor, same contract as resolveTrackAtDtg.
 * @param {boolean} [trailEnabled=false]
 * @param {number} [trailWindowDays=7]
 * @returns {Array<{markingId: string, feature: object}>} one entry per expanded sub-feature,
 *   each `feature` a plain GeoJSON Feature whose `properties` already carries `tacticalSidc`/
 *   `markingId`/`isCurrent` (mirroring syncTacticalSource's `_props` stamp) plus a stable `id`
 *   (`${markingId}-${i}`) suitable for `feature.id` / OL `setId`.
 */
export function expandTacticalMarkingsToFeatures(markings, viewportOpts = {}, cursorDtg, trailEnabled = false, trailWindowDays = 7) {
    const tacticals = (markings || []).filter((m) => m.elementType === 'tactical-line' || m.elementType === 'tactical-area' || m.elementType === 'tactical-point');
    const maxAgeDays = trailEnabled ? trailWindowDays : 0;
    const out = [];
    for (const m of tacticals) {
        const resolved = resolveTrackAtDtg(m, cursorDtg, maxAgeDays);
        if (!resolved.visible || !resolved.snapshot) continue;
        const props = resolved.snapshot.props || {};
        if (!props.sidc) continue;
        const geometry = resolved.snapshot.geometry;
        if (!geometry) continue;
        const coords =
            m.elementType === 'tactical-point' ? (geometry.type === 'Point' ? [geometry.coordinates] : null) :
            geometry.type === 'Polygon'         ? geometry.coordinates[0] :
            geometry.coordinates;
        if (!coords) continue;
        const storedMods = props.sidcModifiers
            ? (typeof props.sidcModifiers === 'string' ? JSON.parse(props.sidcModifiers) : props.sidcModifiers)
            : {};
        const geojson = renderTacticalGraphic(props.sidc, coords, storedMods, viewportOpts);
        if (!geojson?.features) continue;
        geojson.features.forEach((f, i) => {
            out.push({
                markingId: m.markingId,
                feature: {
                    ...f,
                    id: `${m.markingId}-${i}`,
                    properties: { ...(f.properties || {}), tacticalSidc: props.sidc, markingId: m.markingId, isCurrent: resolved.isCurrent },
                },
            });
        });
    }
    return out;
}

/**
 * Convenience wrapper: expand tactical markings and read them straight into OL Features via the
 * shared `FORMAT` (EPSG:4326 -> EPSG:3857), with `_props` stamped for `tacticalFeatureStyle`.
 * Used by the live editor's syncTacticalSource (LYR_TACTICAL population) so that call site stays
 * a thin wrapper around this module instead of re-implementing the GeoJSON->OL conversion.
 *
 * @returns {Array<import('ol/Feature').default>}
 */
export function expandTacticalMarkingsToOlFeatures(markings, viewportOpts, cursorDtg, trailEnabled, trailWindowDays) {
    const expanded = expandTacticalMarkingsToFeatures(markings, viewportOpts, cursorDtg, trailEnabled, trailWindowDays);
    return expanded.map(({ feature }) => {
        const olF = FORMAT.readFeature(feature);
        olF.setId(feature.id);
        olF.set('_props', feature.properties);
        return olF;
    });
}
